import { EntityType } from '@frontend/common';
import { Event } from '@frontend/events/types';
import React from 'react';

import { platformEntityTypeForKey } from '../platform-object-badge/platform-entity-type-by-key';
import { PlatformObjectBadge } from '../platform-object-badge/platform-object-badge.component';

const EVENT_REFERENCE_KEYS = [
    'actor',
    'location',
    'user_id',
    'spot_id',
    'module_id',
    'slot_id',
    'transaction_id',
    'colli_id',
    'package_id',
    'product_id',
    'equipment_id',
    'device_id',
    'iot_id',
    'locker_id',
    'problem_id',
    'user_interface_id',
    'workflow_id',
    'identification_id'
] as const satisfies readonly (keyof Event)[];

type EventReferenceKey = (typeof EVENT_REFERENCE_KEYS)[number];

function entityTypeForEventKey(key: EventReferenceKey): EntityType | undefined {
    if (key === 'actor') return EntityType.USER;
    if (key === 'location') return EntityType.SPOT;
    return platformEntityTypeForKey(key);
}

function parentIdsForEventKey(event: Event, key: EventReferenceKey): Record<string, string> | undefined {
    if (key === 'slot_id') {
        return {
            ...(event.spot_id && { spot_id: event.spot_id }),
            ...(event.module_id && { module_id: event.module_id })
        };
    }
    if (key === 'colli_id' && event.transaction_id) {
        return { transaction_id: event.transaction_id };
    }
    return undefined;
}

export interface PlatformEventReferenceBadgesProps {
    event: Event;
    className?: string;
    excludeKeys?: EventReferenceKey[];
}

/** Platform counterpart of EventReferenceBadges: every reference opens on the platform detail stack. */
export const PlatformEventReferenceBadges = ({ event, className, excludeKeys }: PlatformEventReferenceBadgesProps) => {
    const badges = EVENT_REFERENCE_KEYS.flatMap((key) => {
        const id = event[key];
        const entityType = entityTypeForEventKey(key);
        if (excludeKeys?.includes(key) || id == null || !entityType) {
            return [];
        }
        return [
            <PlatformObjectBadge
                key={`${key}-${id}`}
                id={id}
                entityType={entityType}
                parentIds={parentIdsForEventKey(event, key)}
            />
        ];
    });

    if (badges.length === 0) {
        return null;
    }

    return <div className={`flex w-full flex-wrap items-center gap-2 ${className ?? ''}`}>{badges}</div>;
};
